import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";
import { environment } from "src/environments/environment";
import { SesionService } from "src/app/core/service/sesion.service";
import { TermsConditionsDialogComponent } from "src/app/shared/dialogs/terms-conditions-dialog/terms-conditions-dialog.component";
import { ContactInformation, UploadFileContactInformation } from "./model/potential-customer";
import { ShadowScrollService } from "src/app/core/service/utils/shadow-scroll.service";
import { DocumentId } from "src/app/core/model/constans";
import { CustomerInformationService } from "./service/customer-information.service";
import { FileUploadService } from "../file-upload/service/file-upload.service";
import { ContactOtpChannel, ContactOtpDialogResult, ContactOtpState } from "./model/contact-otp-state";
import { ContactOtpDialogComponent } from "./otp-dialog/contact-otp-dialog.component";
import { ContactPhone, getAllPhoneConfigs, PhoneConfig, PhoneCountry, PHONE_CONFIGS } from "./model/phone-config";
import { buildLegacyPhone, buildOtpPhone, isNationalPhoneValid } from "./contact-phone.util";

@Component({
  selector: "app-contact-information",
  templateUrl: "./contact-information.component.html",
  styleUrls: ["./contact-information.component.scss"],
})
export class ContactInformationComponent implements OnInit, OnDestroy {
  readonly enableEmailOtp = !!environment.features?.enableEmailOtp;
  readonly enablePhoneOtp = !!environment.features?.enablePhoneOtp;
  readonly phoneCountries = getAllPhoneConfigs();
  readonly phoneCountryControl = new FormControl<PhoneCountry>('AR', { nonNullable: true });
  readonly areaCodeControl = new FormControl('', { nonNullable: true, validators: [
    Validators.required, Validators.pattern(this.enablePhoneOtp ? /^[1-9]\d{1,3}$/ : /^0\d{2,4}$/),
  ] });
  readonly phoneNumberControl = new FormControl('', { nonNullable: true, validators: [
    Validators.required, Validators.pattern(this.enablePhoneOtp ? /^\d{6,8}$/ : /^15\d{6,8}$/),
  ] });
  emailVerificationAttempted = false;
  phoneVerificationAttempted = false;
  private readonly destroyed = new Subject<void>();
  private readonly otpStates: Partial<Record<ContactOtpChannel, ContactOtpState>> = {};
  private otpDialog?: { channel: ContactOtpChannel; ref: MatDialogRef<ContactOtpDialogComponent, ContactOtpDialogResult> };
  files: any[] = [];
  contactForm: FormGroup;
  submitted: boolean;
  loading: boolean;
  sumeTime: number;
  telephoneNumber: string;
  secondaryTelephoneNumber:string;
  secondaryTelephoneIncompleteFields:boolean;
  timeA = {
    start: 6,
    end: 14,
  };
  timeB = {
    start: 14,
    end: 23,
  }
  src: string = "assets/terms-and-conditions.pdf";
  @Output()
  onComplete = new EventEmitter<number>();

  @Output()
  onBack = new EventEmitter();

  constructor(
    private fb: FormBuilder,
    private sesion: SesionService,
    private dialog: MatDialog,
    public shadowScroll: ShadowScrollService,
    private serviceFile: FileUploadService,
    private service: CustomerInformationService,
    private element: ElementRef<HTMLElement>
  ) {
    this.submitted = false;
    this.loading = false;
    this.sumeTime = 0;
    this.telephoneNumber = '';
    this.secondaryTelephoneNumber = '';
    this.secondaryTelephoneIncompleteFields = false
    this.setFiles();
    this.contactForm = this.fb.group({
      email: [
        "",
        [Validators.required, Validators.maxLength(50), Validators.pattern(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)],
      ],
      phoneCountry: this.phoneCountryControl,
      telephoneFeature: this.areaCodeControl,
      telephoneNumber: this.phoneNumberControl,
      secondaryTelephoneNumber: ["", Validators.pattern(/^\d{6,8}$/)],
      secondaryTelephoneFeature: ["", Validators.pattern(/^0\d{2,4}$/)],
      samePhoneAtPremises: ["yes", Validators.required],
      timeAStart: [""],
      timeAEnd: [""],
      timeBStart: [""],
      timeBEnd: [""],
      uploadFile0: [""],
      uploadFile1: [""],
      conditionsAndTerms: ["", Validators.requiredTrue],
    });
  }

  setFiles() {
    this.files = [
      {
        label: "Foto del interior del Comercio (opcional)",
        subtitle: 'Adjunta una foto del interior del Comercio',
        fileId: "file 1",
        file: null,
        extensions: [".png", ".jpeg", ".jpg"],
        size: "20",
        isImg: true,
        documentId: DocumentId.FOTO_COMERCIO,
      },
      {
        label: "Foto del exterior del Comercio (opcional)",
        subtitle: 'Adjunta una foto del exterior del Comercio',
        fileId: "file 2",
        file: null,
        extensions: [".png", ".jpeg", ".jpg"],
        size: "20",
        isImg: true,
        documentId: DocumentId.FOTO_COMERCIO,
      },
    ];
  }

  ngOnInit(): void {
    this.phoneCountryControl.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(country => {
      if (!this.enablePhoneOtp) {
        return;
      }
      this.areaCodeControl.setValidators(country === 'AR'
        ? [Validators.required, Validators.pattern(/^[1-9]\d{1,3}$/)]
        : [Validators.pattern(/^\d*$/)]);
      this.phoneNumberControl.setValidators([
        Validators.required,
        Validators.pattern(country === 'AR' ? /^\d{6,8}$/ : /^\d+$/),
      ]);
      this.areaCodeControl.updateValueAndValidity({ emitEvent: false });
      this.phoneNumberControl.updateValueAndValidity({ emitEvent: false });
    });
    ['email', 'phoneCountry', 'telephoneFeature', 'telephoneNumber'].forEach(controlName => {
      this.contactForm.controls[controlName].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
        const channel: ContactOtpChannel = controlName === 'email' ? 'email' : 'phone';
        delete this.otpStates[channel];
        if (this.otpDialog?.channel === channel) {
          this.otpDialog.ref.close();
        }
      });
    });
    this.contactForm.controls['samePhoneAtPremises'].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
      const controls = [
        this.contactForm.controls['secondaryTelephoneFeature'],
        this.contactForm.controls['secondaryTelephoneNumber'],
      ];
      controls.forEach(control => {
        if (value === 'no') {
          control.addValidators(Validators.required);
        } else {
          control.removeValidators(Validators.required);
        }
      });
      if (value === 'yes') {
        this.contactForm.patchValue({ secondaryTelephoneFeature: '', secondaryTelephoneNumber: '' });
      }
      controls.forEach(control => control.updateValueAndValidity());
    });
    this.contactForm.controls["uploadFile0"].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.serviceFile.hideSpinner();
    });

    this.contactForm.controls["uploadFile1"].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.serviceFile.hideSpinner();
    });

    const controlsToWatch = [
    'timeAStart',
    'timeAEnd',
    'timeBStart',
    'timeBEnd'
    ];

    controlsToWatch.forEach(controlName => {
      this.contactForm.controls[controlName].valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
        this.onTimeChange();
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    this.otpDialog?.ref.close();
  }

  get emailVerified(): boolean {
    return this.isVerified('email');
  }

  get phoneVerified(): boolean {
    return this.enablePhoneOtp && this.isVerified('phone');
  }

  get displayedPhone(): string {
    if (!this.enablePhoneOtp) {
      return `${this.areaCodeControl.value} ${this.phoneNumberControl.value}`;
    }
    const number = this.phoneNumberControl.value;
    const formatted = this.phoneCountryControl.value === 'AR' && number.length === 7
      ? `${number.slice(0, 3)}-${number.slice(3)}` : number;
    return `${this.selectedPhoneCountry.code} ${this.areaCodeControl.value} ${formatted}`.replace(/\s+/g, ' ').trim();
  }

  changeContact(channel: ContactOtpChannel): void {
    delete this.otpStates[channel];
    if (channel === 'email') {
      this.emailVerificationAttempted = false;
    } else {
      this.phoneVerificationAttempted = false;
    }
  }

  get phoneValid(): boolean {
    return this.areaCodeControl.valid && this.phoneNumberControl.valid &&
      (!this.enablePhoneOtp || this.phoneCountryControl.valid) &&
      !this.validationLenthTelephone('principal');
  }

  get selectedPhoneCountry(): PhoneConfig {
    return PHONE_CONFIGS[this.phoneCountryControl.value];
  }

  get phoneInput(): ContactPhone {
    return {
      country: this.phoneCountryControl.value,
      areaCode: this.areaCodeControl.value,
      phone: this.phoneNumberControl.value,
    };
  }

  get phoneLengthMessage(): string {
    if (!this.enablePhoneOtp) {
      return 'Longitud del número celular inválido (13 dígitos)';
    }
    switch (this.phoneCountryControl.value) {
      case 'AR': return 'El código de área y el número deben sumar 10 dígitos, sin el 0 ni el prefijo 15.';
      case 'CL': return 'El teléfono debe tener 9 dígitos entre área y número.';
      case 'BR': return 'El teléfono debe tener entre 10 y 11 dígitos entre área y número.';
      case 'PY': return 'El teléfono debe tener entre 8 y 10 dígitos entre área y número.';
    }
  }

  openOtp(channel: ContactOtpChannel): void {
    const enabled = channel === 'email' ? this.enableEmailOtp : this.enablePhoneOtp;
    if (!enabled || this.otpDialog || this.isVerified(channel)) {
      return;
    }
    if (channel === 'email') {
      this.emailVerificationAttempted = true;
    } else {
      this.phoneVerificationAttempted = true;
    }
    const controls = channel === 'email' ? ['email'] : ['telephoneFeature', 'telephoneNumber', 'phoneCountry'];
    controls.forEach(name => this.contactForm.controls[name].markAsTouched());
    const valid = channel === 'email' ? this.contactForm.controls['email'].valid : this.phoneValid;
    if (!valid) {
      const invalidControl = controls.find(name => this.contactForm.controls[name].invalid) ?? controls[0];
      this.element.nativeElement.querySelector<HTMLInputElement>(`[formControlName="${invalidControl}"] input`)?.focus();
      return;
    }
    const recipient = this.recipient(channel);
    const state = this.otpStates[channel] ?? { channel, recipient, status: 'confirm' };
    if (channel === 'phone') {
      state.phoneDisplay = {
        country: this.selectedPhoneCountry.key,
        countryLabel: this.selectedPhoneCountry.label,
        countryCode: this.selectedPhoneCountry.code,
        nationalNumber: this.displayedPhone.slice(this.selectedPhoneCountry.code.length).trim(),
      };
    }
    this.otpStates[channel] = state;
    const ref = this.dialog.open<ContactOtpDialogComponent, ContactOtpState, ContactOtpDialogResult>(
      ContactOtpDialogComponent,
      { width: '550px', maxWidth: '95vw', panelClass: 'contact-otp-dialog', data: state },
    );
    this.otpDialog = { channel, ref };
    ref.afterClosed().pipe(takeUntil(this.destroyed)).subscribe(result => {
      if (this.otpDialog?.ref === ref) {
        this.otpDialog = undefined;
      }
      if (result?.action === 'edit' && this.otpStates[channel] === state) {
        delete this.otpStates[channel];
        const control = channel === 'email' ? 'email' : 'telephoneFeature';
        this.element.nativeElement.querySelector<HTMLInputElement>(`[formControlName="${control}"] input`)?.focus();
      }
    });
  }

  private recipient(channel: ContactOtpChannel): string {
    return channel === 'email' ? this.contactForm.controls['email'].value :
      this.enablePhoneOtp ? buildOtpPhone(this.phoneInput) : '';
  }

  private isVerified(channel: ContactOtpChannel): boolean {
    const state = this.otpStates[channel];
    return state?.status === 'verified' && state.recipient === this.recipient(channel);
  }

  back() {
    this.onBack.emit();
  }

  onTimeChange(){
      let timeA = this.subtractTime(
        this.contactForm.controls["timeAEnd"].value,
        this.contactForm.controls["timeAStart"].value
      );
      let timeB = this.subtractTime(
        this.contactForm.controls["timeBEnd"].value,
        this.contactForm.controls["timeBStart"].value
      );
      this.sumeTime = timeA + timeB;
  }

  onSubmit() {
    if (!this.loading) {
      this.submitted = true;
      let timeA = this.subtractTime(
        this.contactForm.controls["timeAEnd"].value,
        this.contactForm.controls["timeAStart"].value
      );
      let timeB = this.subtractTime(
        this.contactForm.controls["timeBEnd"].value,
        this.contactForm.controls["timeBStart"].value
      );
      this.sumeTime = timeA + timeB;
      this.telephoneNumber = this.contactForm.controls["telephoneFeature"].value + this.contactForm.controls["telephoneNumber"].value;
      this.secondaryTelephoneNumber = this.contactForm.controls["secondaryTelephoneFeature"].value + this.contactForm.controls["secondaryTelephoneNumber"].value;
      this.secondaryTelephoneIncompleteFields = this.secondaryTelephoneFieldsCompletes();
      let validSecondaryTelephone = (!(this.secondaryTelephoneNumber.length != 11) && !this.secondaryTelephoneIncompleteFields) || !this.secondaryTelephoneNumber
      
      if (this.contactForm.valid && this.sumeTime >= 4 && this.phoneValid && validSecondaryTelephone
        && (this.contactForm.controls['samePhoneAtPremises'].value !== 'no' || !!this.secondaryTelephoneNumber)
        && (!this.enableEmailOtp || this.emailVerified)
        && (!this.enablePhoneOtp || this.phoneVerified)) {
        this.formComplete();
      }     
    }
  }

  secondaryTelephoneFieldsCompletes():boolean{
    let secondaryTelephoneFeature = this.contactForm.controls["secondaryTelephoneFeature"].value
    let secondaryTelephoneNumber = this.contactForm.controls["secondaryTelephoneNumber"].value
    return (secondaryTelephoneFeature && !secondaryTelephoneNumber) || (!secondaryTelephoneFeature && secondaryTelephoneNumber)
  }
  subtractTime(time1: string, time2: string): number {
    // convertir las cadenas de texto en horas y minutos
    const [hours1, minutes1] = time1.split(":").map(Number);
    const [hours2, minutes2] = time2.split(":").map(Number);

    // calcular la diferencia en minutos
    const totalMinutes1 = hours1 * 60 + minutes1;
    const totalMinutes2 = hours2 * 60 + minutes2;
    const diffMinutes = totalMinutes1 - totalMinutes2;

    // convertir la diferencia en horas y minutos
    const hoursDiff = Math.floor(diffMinutes / 60);
    let minutesDiff = diffMinutes % 60;
    if (minutesDiff == 30) {
      minutesDiff = 50;
    }
    let formattedDiff = Number(hoursDiff + "." + minutesDiff);
    return Number.isNaN(formattedDiff) ? 0 : formattedDiff
  }

  formComplete() {
    // AR retains its legacy mapping; other countries use the approved international representation.
    const telephone = this.enablePhoneOtp
      ? buildLegacyPhone(this.phoneInput) ?? buildOtpPhone(this.phoneInput)
      : `${this.areaCodeControl.value} ${this.phoneNumberControl.value}`;
    const ContacInformation: ContactInformation = {
      email: this.contactForm.controls["email"].value,
      telephone,
      secondaryTelephone: this.contactForm.controls["samePhoneAtPremises"].value === 'no'
        ? this.contactForm.controls["secondaryTelephoneFeature"].value +" "+ this.contactForm.controls["secondaryTelephoneNumber"].value
        : "",
      timeA: {
        start: this.contactForm.controls["timeAStart"].value,
        end: this.contactForm.controls["timeAEnd"].value,
      },
      timeB: {
        start: this.contactForm.controls["timeBStart"].value,
        end: this.contactForm.controls["timeBEnd"].value,
      },
    };
    const uploadFileOptional: UploadFileContactInformation = {
      uploadFile0: this.contactForm.controls["uploadFile0"].value,
      uploadFile1: this.contactForm.controls["uploadFile1"].value,
    };

    this.sesion.setItem(
      "contact-information",
      JSON.stringify(ContacInformation)
    );
    this.service.saveFiles(uploadFileOptional);
    this.onComplete.emit(7);
  }

  openTermsDialog() {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(TermsConditionsDialogComponent, {
      height: isMobile ? "100%" : "85%",
      width: isMobile ? "100%" : "60%",
      data: {
        pdfSrc: this.src,
      },
    });
  }

  openTermsDialogOnKeyDown(event: KeyboardEvent) {
    this.openTermsDialog();
  }

  validationPicker(start: any, end: any, touchOne: boolean, touchTwo: boolean) {
    if (start >= end && touchOne && touchTwo && start && end) {
      return false;
    }
    return true;
  }

  @HostListener("scroll", ["$event"])
  onScroll(e: any) {
    this.shadowScroll.scroll(e);
  }

  validationLenthTelephone(type:string):boolean{
    // Validacion para los inputs de telefonos, para que corespondan a uuna catiddad de digitos valida segun el tipo
    let fullnumber;
    let max;
    if (type == 'principal'){
      if (this.enablePhoneOtp) {
        return !!(this.areaCodeControl.value || this.phoneNumberControl.value) &&
          !isNationalPhoneValid(this.phoneInput);
      }
      fullnumber = this.areaCodeControl.value + this.phoneNumberControl.value;
      max = 13;
    }else {
      fullnumber = this.contactForm.controls["secondaryTelephoneFeature"].value + this.contactForm.controls["secondaryTelephoneNumber"].value;
      max = 11
    }
    return fullnumber.length != max && Number(fullnumber) !== 0
  }
}
